import { useEffect, useState } from "react";
import { SITE, SERVICES } from "@/lib/site";
import { getDistrito } from "@/data/geo";

export type MessageVariant = "formal" | "directo";
export type MessageIntent = "cotizar" | "consultar";

/**
 * Horario de atención (hora de Lima) usado solo para la nota de "fuera de
 * horario" del mensaje de WhatsApp. No existe hoy un campo de horario en
 * `crm_configuracion` — constante simple de ajustar en vez de una feature
 * de configuración que nadie pidió. Confirmado con el usuario: lun-sáb 9-19.
 */
export const BUSINESS_HOURS = {
  timezone: "America/Lima",
  days: [1, 2, 3, 4, 5, 6], // 0 = domingo … 6 = sábado (domingo cerrado)
  startHour: 9,
  endHour: 19,
};

function limaWeekdayAndHour(now: Date): { weekday: number; hour: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_HOURS.timezone,
    weekday: "short",
    hour: "numeric",
    hourCycle: "h23",
  }).formatToParts(now);
  const weekdayShort = parts.find((p) => p.type === "weekday")?.value ?? "Sun";
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return { weekday: weekdays.indexOf(weekdayShort), hour };
}

export function isBusinessHours(now: Date = new Date()): boolean {
  const { weekday, hour } = limaWeekdayAndHour(now);
  return (
    BUSINESS_HOURS.days.includes(weekday) &&
    hour >= BUSINESS_HOURS.startHour &&
    hour < BUSINESS_HOURS.endHour
  );
}

export function withBusinessHoursNote(message: string, now: Date = new Date()): string {
  if (isBusinessHours(now)) return message;
  return `${message} Sé que puede ser fuera de su horario de atención — quedo pendiente de su respuesta.`;
}

/** Mensaje genérico cuando la página no tiene un tema propio (home, footer, nav, etc.). */
export function genericMessage(variant: MessageVariant): string {
  return variant === "formal"
    ? "Hola, buenos días. Quisiera coordinar una propuesta para activar mi marca."
    : SITE.whatsappMsg;
}

/** Mensaje para un tema puntual (servicio, servicio+distrito, post de blog). */
export function buildTopicMessage(
  topic: string,
  intent: MessageIntent,
  variant: MessageVariant,
): string {
  if (intent === "consultar") {
    return variant === "formal"
      ? `Hola, buenas. Quisiera hacerles una consulta sobre ${topic}. ¿Podrían ayudarme?`
      : `Hola, tengo una duda sobre ${topic}. ¿Me ayudan?`;
  }
  return variant === "formal"
    ? `Hola, buenos días. Quisiera coordinar una propuesta para ${topic}.`
    : `Hola, quiero cotizar ${topic}. ¿Coordinamos una propuesta?`;
}

export function messageTopicForService(serviceName: string): string {
  return serviceName;
}

export function messageTopicForGeoService(serviceName: string, distritoName: string): string {
  return `${serviceName} en ${distritoName}`;
}

export function messageTopicForBlogPost(title: string): string {
  return `el artículo "${title}"`;
}

export interface PathTopic {
  topic?: string;
  intent: MessageIntent;
  topicId: string;
}

/**
 * Resuelve el "tema" del mensaje a partir del pathname actual — usado por
 * el FAB, que se monta a nivel de root sin props de página. Las páginas de
 * servicio/geo/blog en sí no usan esto: ya tienen el dato exacto (nombre de
 * servicio, distrito, post) en scope y lo pasan directo.
 */
export function resolveTopicFromPath(
  pathname: string,
  opts: { blogTitle?: string } = {},
): PathTopic {
  const service = SERVICES.find((s) => pathname === s.slug || pathname.startsWith(`${s.slug}/`));
  if (service) {
    const rest = pathname.slice(service.slug.length).replace(/^\//, "").replace(/\/$/, "");
    const topicId = service.slug.slice(1);
    if (rest) {
      const distrito = getDistrito(rest);
      if (distrito) {
        return {
          topic: messageTopicForGeoService(service.name, distrito.name),
          intent: "cotizar",
          topicId: `${topicId}-${distrito.slug}`,
        };
      }
    }
    return { topic: messageTopicForService(service.name), intent: "cotizar", topicId };
  }
  // Páginas informativas: quien lee un post, el blog o «Nosotros» aún no pide propuesta, así que el
  // mensaje consulta en vez de cotizar. En el post se nombra el artículo si el layout lo conoce.
  if (pathname.startsWith("/blog/categoria/") || pathname.replace(/\/$/, "") === "/blog") {
    return {
      topic: "sus contenidos sobre marketing BTL",
      intent: "consultar",
      topicId: "blog-listado",
    };
  }
  if (pathname.startsWith("/blog/")) {
    return {
      topic: opts.blogTitle ? messageTopicForBlogPost(opts.blogTitle) : "un artículo del blog",
      intent: "consultar",
      topicId: "blog",
    };
  }
  if (pathname === "/nosotros") {
    return { topic: "la agencia y su forma de trabajar", intent: "consultar", topicId: "nosotros" };
  }
  // Empleo: quien postula no es cliente, así que nada de «cotizar» ni «activar mi marca».
  if (pathname === "/postular" || pathname === "/mi-perfil") {
    return {
      topic: "cómo postular como promotora o promotor",
      intent: "consultar",
      topicId: "postular",
    };
  }
  // Comerciales sin servicio propio: ya decidió pedir propuesta, solo falta darle contexto.
  if (pathname === "/cotizar") {
    return { topic: "la cotización de mi proyecto", intent: "consultar", topicId: "cotizar" };
  }
  if (pathname.startsWith("/cobertura")) {
    return { topic: "activaciones BTL en Lima", intent: "cotizar", topicId: "cobertura" };
  }
  return { intent: "cotizar", topicId: "generico" };
}

const VARIANT_STORAGE_KEY = "iy_wa_variant";
let cachedVariant: MessageVariant | null = null;

/**
 * Asigna (o recupera) la variante de tono A/B para esta sesión de
 * navegador, para que todos los WhatsAppLink de la misma visita muestren un
 * mensaje coherente. Cache en memoria + sessionStorage, mismo patrón que
 * otros cache TTL del repo. `deps` es solo para tests (no mockear globals).
 */
export function getOrAssignVariant(deps?: {
  storage?: Pick<Storage, "getItem" | "setItem">;
  random?: () => number;
}): MessageVariant {
  if (!deps && cachedVariant) return cachedVariant;
  try {
    const storage = deps?.storage ?? sessionStorage;
    const stored = storage.getItem(VARIANT_STORAGE_KEY);
    if (stored === "formal" || stored === "directo") {
      if (!deps) cachedVariant = stored;
      return stored;
    }
    const random = deps?.random ?? Math.random;
    const assigned: MessageVariant = random() < 0.5 ? "formal" : "directo";
    storage.setItem(VARIANT_STORAGE_KEY, assigned);
    if (!deps) cachedVariant = assigned;
    return assigned;
  } catch {
    return "directo";
  }
}

/**
 * Variante de tono resuelta para el render actual. En SSR/primer paint
 * siempre "directo" (mismo tono que el mensaje genérico de siempre, sin
 * mismatch de hidratación) — la variante real de sesión se resuelve tras
 * montar, en el cliente.
 */
export function useMessageVariant(): MessageVariant {
  const [variant, setVariant] = useState<MessageVariant>("directo");
  useEffect(() => {
    setVariant(getOrAssignVariant());
  }, []);
  return variant;
}
